import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';

interface DropCollectionRow {
  id: string;
  name: string | null;
  user_id: string;
  created_at: string;
  media_url: string | null;
  media_type: string | null;
  total_cost_cents: number;
  fomo_fee_cents: number;
  creator_share_cents: number;
  status: string;
  payment_status: string | null;
  admin_notes: string | null;
}

interface RarityRow {
  id: string;
  collection_id: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'legendary' | string;
  count: number;
  unit_value_cents: number;
  cost_cents: number;
}

interface ProfileLite {
  id: string;
  username: string | null;
  display_name: string | null;
  email: string | null;
}

const currency = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const statusVariant = (status: string) => {
  switch (status) {
    case 'pending_review':
      return 'outline' as const;
    case 'approved':
      return 'default' as const;
    case 'rejected':
      return 'destructive' as const;
    default:
      return 'secondary' as const;
  }
};

const DropsAdminManager: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<DropCollectionRow[]>([]);
  const [rarities, setRarities] = useState<Record<string, RarityRow[]>>({});
  const [profiles, setProfiles] = useState<Record<string, ProfileLite>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});

  const refresh = async () => {
    try {
      setLoading(true);
      const { data: collections, error } = await supabase
        .from('drop_collections')
        .select(
          'id, name, user_id, created_at, media_url, media_type, total_cost_cents, fomo_fee_cents, creator_share_cents, status, payment_status, admin_notes'
        )
        .eq('status', 'pending_review')
        .order('created_at', { ascending: true });

      if (error) throw error;

      const list = (collections || []) as DropCollectionRow[];
      setRows(list);

      // Prefill notes from server
      const notesMap: Record<string, string> = {};
      list.forEach((c) => (notesMap[c.id] = c.admin_notes || ''));
      setNotes(notesMap);

      const ids = list.map((c) => c.id);
      const userIds = Array.from(new Set(list.map((c) => c.user_id)));

      if (ids.length) {
        const { data: raritiesData, error: rarErr } = await supabase
          .from('drop_rarities')
          .select('id, collection_id, rarity, count, unit_value_cents, cost_cents')
          .in('collection_id', ids);
        if (rarErr) throw rarErr;
        const byCollection: Record<string, RarityRow[]> = {};
        (raritiesData || []).forEach((r) => {
          if (!byCollection[r.collection_id]) byCollection[r.collection_id] = [];
          byCollection[r.collection_id].push(r as RarityRow);
        });
        setRarities(byCollection);
      } else {
        setRarities({});
      }

      if (userIds.length) {
        const { data: profs } = await supabase
          .from('profiles')
          .select('id, username, display_name, email')
          .in('id', userIds);
        const map: Record<string, ProfileLite> = {};
        (profs || []).forEach((p) => (map[p.id] = p as ProfileLite));
        setProfiles(map);
      } else {
        setProfiles({});
      }
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed loading drops', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pendingCount = useMemo(() => rows.length, [rows]);

  const handleDecision = async (id: string, decision: 'approved' | 'rejected') => {
    if (!user?.id) return;
    try {
      const admin_notes = notes[id] || null;
      const { error } = await supabase
        .from('drop_collections')
        .update({
          status: decision,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          admin_notes,
        })
        .eq('id', id);
      if (error) throw error;

      toast({ title: `Collection ${decision}`, description: `Status set to ${decision}.` });
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e: any) {
      toast({ title: 'Action failed', description: e.message || 'Please try again', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Drops Review</h2>
        <div className="flex items-center gap-3">
          <Badge variant={pendingCount ? 'default' : 'secondary'}>Pending: {pendingCount}</Badge>
          <Button variant="secondary" onClick={refresh}>Refresh</Button>
        </div>
      </div>

      {loading ? (
        <Card>
          <CardHeader>
            <CardTitle>Loading pending collections…</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              <div className="space-y-2">
                <div className="h-4 w-32 bg-muted/50 rounded-md" />
                <div className="h-4 w-48 bg-muted/50 rounded-md" />
              </div>
              <div className="space-y-2">
                <div className="h-4 w-32 bg-muted/50 rounded-md" />
                <div className="h-4 w-48 bg-muted/50 rounded-md" />
              </div>
            </div>
          </CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No pending collections</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">When creators submit and pay, their collections will appear here for review.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {rows.map((c) => {
            const profile = profiles[c.user_id];
            const r = rarities[c.id] || [];
            return (
              <Card key={c.id} className="overflow-hidden">
                <CardHeader className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <CardTitle className="text-xl">{c.name || 'Untitled Collection'}</CardTitle>
                      <div className="text-sm text-muted-foreground">
                        Creator: {profile?.display_name || profile?.username || c.user_id.slice(0, 8)} • Created {new Date(c.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={statusVariant(c.status)}>{c.status}</Badge>
                      {c.payment_status && <Badge variant="outline">{c.payment_status}</Badge>}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="grid gap-6 md:grid-cols-3">
                  <div className="md:col-span-1">
                    {c.media_type === 'image' && c.media_url ? (
                      <img
                        src={c.media_url}
                        alt={(c.name || 'Drop') + ' preview image'}
                        loading="lazy"
                        className="w-full h-auto rounded-md"
                      />
                    ) : (
                      <div className="text-sm text-muted-foreground">No image preview. Media type: {c.media_type || 'unknown'}</div>
                    )}
                    <div className="mt-3 space-y-1 text-sm">
                      <div>Total: {currency(c.total_cost_cents)}</div>
                      <div>Creator share: {currency(c.creator_share_cents)}</div>
                      <div>FOMO fee: {currency(c.fomo_fee_cents)}</div>
                    </div>
                  </div>

                  <div className="md:col-span-1 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Rarity</TableHead>
                          <TableHead className="text-right">Count</TableHead>
                          <TableHead className="text-right">Unit</TableHead>
                          <TableHead className="text-right">Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {r.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-muted-foreground">No rarities defined</TableCell>
                          </TableRow>
                        ) : (
                          r.map((rr) => (
                            <TableRow key={rr.id}>
                              <TableCell className="capitalize">{rr.rarity}</TableCell>
                              <TableCell className="text-right">{rr.count}</TableCell>
                              <TableCell className="text-right">{currency(rr.unit_value_cents)}</TableCell>
                              <TableCell className="text-right">{currency(rr.cost_cents)}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="md:col-span-1 flex flex-col gap-3">
                    <label className="text-sm font-medium">Admin notes</label>
                    <Textarea
                      placeholder="Reasoning, restrictions, or guidance for the creator…"
                      value={notes[c.id] || ''}
                      onChange={(e) => setNotes((prev) => ({ ...prev, [c.id]: e.target.value }))}
                      rows={6}
                    />
                    <div className="flex gap-2 justify-end">
                      <Button variant="secondary" onClick={() => handleDecision(c.id, 'rejected')}>Reject</Button>
                      <Button onClick={() => handleDecision(c.id, 'approved')}>Approve</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DropsAdminManager;
